import { useCallback, useMemo } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { GenerateProductRequestDto } from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import { generatedAIProductData as generatedAIProductDataSelector } from 'domains/Catalog/Products/productsSlice/productSelectors';
import {
  generateProduct as generateProductAction,
  cleanGeneratedAIProduct as cleanGeneratedAIProductAction,
} from '../../../productsSlice';

function useGenerateProduct() {
  const dispatch = useAppDispatch();
  const allLanguages = useGetAllLanguages();
  const generatedAIProductData = useSelector(generatedAIProductDataSelector);
  const generateAIProduct = useCallback(
    async (requestDto: GenerateProductRequestDto) => {
      const result = await dispatch(generateProductAction(requestDto));
      return unwrapResult(result);
    },
    [dispatch],
  );

  const generatedAIName = useMemo(
    () =>
      allLanguages.reduce((acc, language) => {
        const name =
          generatedAIProductData?.name_description.find(
            (item) => item.language === language,
          )?.name ?? '';
        return {
          ...acc,
          [language]: name,
        };
      }, {}),
    [generatedAIProductData, allLanguages],
  );

  const generatedAIDescription = useMemo(
    () =>
      allLanguages.reduce((acc, language) => {
        const description =
          generatedAIProductData?.name_description.find(
            (item) => item.language === language,
          )?.description ?? '';
        return {
          ...acc,
          [language]: description,
        };
      }, {}),
    [generatedAIProductData, allLanguages],
  );

  const generatedAICategories = useMemo(
    () =>
      generatedAIProductData?.categories.map((category) =>
        category.category_id.toString(),
      ),
    [generatedAIProductData],
  );

  const cleanGeneratedAIProduct = useCallback(() => {
    dispatch(cleanGeneratedAIProductAction());
  }, [dispatch]);
  return {
    generatedAIProductData,
    generatedAIName,
    generatedAIDescription,
    generatedAICategories,
    generateAIProduct,
    cleanGeneratedAIProduct,
  };
}

export default useGenerateProduct;
